
// One row of the pricing matrix: a geography × card-type line with the
// In-Person / Tap to Pay / Online fixed + rate inputs. Each non-empty cell
// maps to one PaymentFeeTemplateLine ( or BusinessPaymentFee on the license
// side ) owned by the hosting controller.
class PaymentFeeMatrixRowCell extends UITableViewCell
{
    private titleLabel:MUILabel = null;
    private fixedFields:{[channel:number]:MUITextField} = {};
    private rateFields:{[channel:number]:MUITextField} = {};

    delegate = null; // matrixCellDidChange(def, channel, fixedDisplay, rateDisplay)

    awakeFromHTML(): void {
        this.titleLabel = MUIOutlet(this, "row-title-lbl", "MUILabel");

        let outlets = [
            { channel: PaymentFeeChannel.InPerson, fixed: "ip-fixed-tf",  rate: "ip-rate-tf"  },
            { channel: PaymentFeeChannel.TapToPay, fixed: "t2p-fixed-tf", rate: "t2p-rate-tf" },
            { channel: PaymentFeeChannel.Online,   fixed: "onl-fixed-tf", rate: "onl-rate-tf" }
        ];

        for (let o of outlets) {
            let fixed = MUIOutlet(this, o.fixed, "MUITextField") as MUITextField;
            fixed.setOnChangeText(this, function(this:PaymentFeeMatrixRowCell, control:MUITextField, value:string){
                this.notifyChange(o.channel);
            });
            this.fixedFields[o.channel] = fixed;

            let rate = MUIOutlet(this, o.rate, "MUITextField") as MUITextField;
            rate.setOnChangeText(this, function(this:PaymentFeeMatrixRowCell, control:MUITextField, value:string){
                this.notifyChange(o.channel);
            });
            this.rateFields[o.channel] = rate;
        }

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private def:PaymentFeeMatrixRowDef = null;
    setRow(def:PaymentFeeMatrixRowDef, linesByChannel:{[channel:number]:PaymentFeeLineLike}, readOnly:boolean){
        this.def = def;
        this.titleLabel.text = MIOLocalizeString(def.titleKey, def.titleDefault);

        for (let channel of PaymentFeeMatrixChannels) {
            let line = linesByChannel[channel];
            this.fixedFields[channel].text = line != null ? PaymentFeeMatrixFixedDisplay(line.transactionFeeFormat) : "";
            this.rateFields[channel].text = line != null ? PaymentFeeMatrixRateDisplay(line.feeFormat) : "";
            this.fixedFields[channel].enabled = !readOnly;
            this.rateFields[channel].enabled = !readOnly;
        }
    }

    private notifyChange(channel:PaymentFeeChannel){
        if (this.def == null || this.delegate == null) return;
        this.delegate.matrixCellDidChange(this.def, channel, this.fixedFields[channel].text, this.rateFields[channel].text);
    }
}

// Rules that do not fit a standard matrix cell: exact countries, other card
// brands, legacy 3DS rows. Full dimension editing, like the old editor,
// plus the channel and 3DS-mode dimensions.
class PaymentFeeAdvancedLineCell extends UITableViewCell
{
    private cardBrandCombobox:MUIComboBox = null;
    private cardCountryTextField:MUITextField = null;
    private fundingSourceCombobox:MUIComboBox = null;
    private cardTypeCombobox:MUIComboBox = null;
    private channelCombobox:MUIComboBox = null;
    private threeDSCombobox:MUIComboBox = null;
    private transactionFeeTextField:MUITextField = null;
    private feeTextField:MUITextField = null;

    awakeFromHTML(): void {
        this.cardBrandCombobox = MUIOutlet(this, "card-brand-cb", "MUIComboBox");
        this.cardBrandCombobox.setOnChangeAction(this, function(this:PaymentFeeAdvancedLineCell, control:MUIComboBox, value:string){
            this.line.cardBrandType = parseInt( value );
        });

        this.cardCountryTextField = MUIOutlet(this, "card-country-tf", "MUITextField");
        this.cardCountryTextField.setOnChangeText(this, function(this:PaymentFeeAdvancedLineCell, control:MUITextField, value:string){
            this.line.cardCountry = value.trim().length == 0 ? null : value.trim();
        });

        this.fundingSourceCombobox = MUIOutlet(this, "funding-source-cb", "MUIComboBox");
        this.fundingSourceCombobox.setOnChangeAction(this, function(this:PaymentFeeAdvancedLineCell, control:MUIComboBox, value:string){
            this.line.fundingSource = parseInt( value );
        });

        this.cardTypeCombobox = MUIOutlet(this, "card-type-cb", "MUIComboBox");
        this.cardTypeCombobox.setOnChangeAction(this, function(this:PaymentFeeAdvancedLineCell, control:MUIComboBox, value:string){
            this.line.cardType = parseInt( value );
        });

        this.channelCombobox = MUIOutlet(this, "channel-cb", "MUIComboBox");
        this.channelCombobox.setOnChangeAction(this, function(this:PaymentFeeAdvancedLineCell, control:MUIComboBox, value:string){
            this.line.channel = parseInt( value );
        });

        this.threeDSCombobox = MUIOutlet(this, "three-ds-cb", "MUIComboBox");
        this.threeDSCombobox.setOnChangeAction(this, function(this:PaymentFeeAdvancedLineCell, control:MUIComboBox, value:string){
            let mode = parseInt( value );
            this.line.threeDSMode = mode;
            // keep the legacy flag coherent for old readers
            this.line.has3DS = ( mode == PaymentFeeThreeDSMode.Only3DS );
        });

        this.transactionFeeTextField = MUIOutlet(this, "transaction-fee-tf", "MUITextField");
        this.transactionFeeTextField.setOnChangeText(this, function(this:PaymentFeeAdvancedLineCell, control:MUITextField, value:string){
            this.line.transactionFeeFormat = value.trim().length == 0 ? "0" : value.trim();
        });

        this.feeTextField = MUIOutlet(this, "fee-tf", "MUITextField");
        this.feeTextField.setOnChangeText(this, function(this:PaymentFeeAdvancedLineCell, control:MUITextField, value:string){
            this.line.feeFormat = value.trim().length == 0 ? "0" : value.trim();
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private line:PaymentFeeLineLike = null;
    setLine(line:PaymentFeeLineLike, readOnly:boolean) {
        this.line = line;

        this.cardBrandCombobox.selectItem( (line.cardBrandType ?? 0).toString() );
        this.cardCountryTextField.text = line.cardCountry;
        this.fundingSourceCombobox.selectItem( (line.fundingSource ?? 0).toString() );
        this.cardTypeCombobox.selectItem( (line.cardType ?? 0).toString() );
        this.channelCombobox.selectItem( (line.channel ?? 0).toString() );
        this.threeDSCombobox.selectItem( (line.threeDSMode ?? 0).toString() );
        this.transactionFeeTextField.text = line.transactionFeeFormat;
        this.feeTextField.text = line.feeFormat;

        this.cardBrandCombobox.enabled = !readOnly;
        this.cardCountryTextField.enabled = !readOnly;
        this.fundingSourceCombobox.enabled = !readOnly;
        this.cardTypeCombobox.enabled = !readOnly;
        this.channelCombobox.enabled = !readOnly;
        this.threeDSCombobox.enabled = !readOnly;
        this.transactionFeeTextField.enabled = !readOnly;
        this.feeTextField.enabled = !readOnly;
    }
}

class PaymentFeeTemplateDetailViewController extends MUIViewController
{
    static newInstance() : PaymentFeeTemplateDetailViewController {
        let vc = new PaymentFeeTemplateDetailViewController("payment-fee-template-detail-view");
        vc.initWithResource("layout/payment_fee_templates/PaymentFeeTemplateDetailView.html");
        return vc;
    }

    private backButton:MUIButton = null;
    private addButton:MUIButton = null;
    private saveButton:MUIButton = null;
    private nameTextField:MUITextField = null;
    private tableView:UITableView = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.backButton = MUIOutlet(this, "back-btn", "MUIButton");
        this.backButton.setAction(this, function(){
            this.navigationController.popViewController();
        });

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, this.addAdvancedLineAction);

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, this.saveAction);

        this.nameTextField = MUIOutlet(this, "name-tf", "MUITextField");
        this.nameTextField.setOnChangeText(this, function(this:PaymentFeeTemplateDetailViewController, textField:MUITextField, value:string){
            this.template.name = value;
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animated?){
        super.viewWillAppear(animated);
        this.updateUI();
    }

    private template:PaymentFeeTemplate = null;
    set item(item:PaymentFeeTemplate){
        this.template = item;
        this.updateUI();
    }

    // Matrix index: cell key -> line, plus the advanced leftovers.
    private lines:PaymentFeeTemplateLine[] = [];
    private cellMap:{[key:string]:PaymentFeeTemplateLine} = {};
    private advancedLines:PaymentFeeTemplateLine[] = [];

    private updateUI(){
        if (this.viewIsLoaded == false || this.template == null) return;

        this.nameTextField.text = this.template.name;

        let predicate = MIOPredicate.predicateWithFormat('template.identifier == "' + this.template.identifier + '" and deletedAt == null');
        DBHelper.queryObjectsWithCompletion("PaymentFeeTemplateLine", null, predicate, ["template"], this, function(this:PaymentFeeTemplateDetailViewController, lines:PaymentFeeTemplateLine[]){
            this.lines = lines;
            this.rebuildIndex();
            this.tableView.reloadData();
        });
    }

    private rebuildIndex(){
        this.cellMap = {};
        this.advancedLines = [];

        for (let line of this.lines){
            if (line.deletedAt != null) continue;
            let key = PaymentFeeMatrixCellKeyForLine(line);
            if (key != null && this.cellMap[key] == null) this.cellMap[key] = line;
            else this.advancedLines.push(line);
        }
    }

    numberOfSections(tableView:UITableView){
        return PaymentFeeMatrixSections.length + 1;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        if (section < PaymentFeeMatrixSections.length) return PaymentFeeMatrixSections[section].rows.length;
        return this.advancedLines.length;
    }

    titleForHeaderInSection(tableView:UITableView, section:number) {
        if (section < PaymentFeeMatrixSections.length) {
            let def = PaymentFeeMatrixSections[section];
            return MIOLocalizeString(def.titleKey, def.titleDefault);
        }
        if (this.advancedLines.length == 0) return null;
        return MIOLocalizeString("ADVANCED_FEE_RULES", "ADVANCED RULES");
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        if (indexPath.section < PaymentFeeMatrixSections.length) {
            let cell = tableView.dequeueReusableCellWithIdentifier("PaymentFeeMatrixRowCell") as PaymentFeeMatrixRowCell;
            let def = PaymentFeeMatrixSections[indexPath.section].rows[indexPath.row];

            let linesByChannel = {};
            for (let channel of PaymentFeeMatrixChannels) {
                linesByChannel[channel] = this.cellMap[ PaymentFeeMatrixCellKey(def, channel) ];
            }

            cell.delegate = this;
            cell.setRow(def, linesByChannel, false);
            return cell;
        }

        let cell = tableView.dequeueReusableCellWithIdentifier("PaymentFeeAdvancedLineCell") as PaymentFeeAdvancedLineCell;
        cell.setLine(this.advancedLines[indexPath.row], false);
        return cell;
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        if (indexPath.section < PaymentFeeMatrixSections.length) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView:UITableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath) {
        if (editingStyle != UITableViewCellEditingStyle.Delete) return;
        if (indexPath.section < PaymentFeeMatrixSections.length) return;

        let line = this.advancedLines[indexPath.row];
        DBHelper.deleteObjectFromMainContext(line, true);
        this.updateUI();
    }

    // A matrix cell changed: both fields empty removes the rule, anything
    // else finds-or-creates the rule for that row + channel and writes the
    // fixed ( EUR ) and rate ( % ) formats.
    matrixCellDidChange(def:PaymentFeeMatrixRowDef, channel:PaymentFeeChannel, fixedDisplay:string, rateDisplay:string){
        let key = PaymentFeeMatrixCellKey(def, channel);
        let line = this.cellMap[key];

        let fixed = PaymentFeeMatrixFixedFormat(fixedDisplay);
        let rate = PaymentFeeMatrixRateFormat(rateDisplay);

        if (fixed == null && rate == null) {
            if (line != null) {
                DBHelper.deleteObjectFromMainContext(line, false);
                delete this.cellMap[key];
                let index = this.lines.indexOf(line);
                if (index >= 0) this.lines.splice(index, 1);
            }
            return;
        }

        if (line == null) {
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            line = MIOEntityDescription.insertNewObjectForEntityForName("PaymentFeeTemplateLine", ad.managedObjectContext) as PaymentFeeTemplateLine;
            line.template = this.template;
            PaymentFeeMatrixApplyDims(line, def, channel);
            this.cellMap[key] = line;
            this.lines.push(line);
        }

        line.transactionFeeFormat = fixed != null ? fixed : "0";
        line.feeFormat = rate != null ? rate : "0";
    }

    private addAdvancedLineAction(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let line = MIOEntityDescription.insertNewObjectForEntityForName("PaymentFeeTemplateLine", ad.managedObjectContext) as PaymentFeeTemplateLine;
        line.template = this.template;
        line.paymentEntityType = PaymentEntityType.Adyen;
        line.cardBrandType = 0;
        line.fundingSource = 0;
        line.cardType = 0;
        line.channel = PaymentFeeChannel.Any;
        line.threeDSMode = PaymentFeeThreeDSMode.Any;
        line.has3DS = false;
        line.transactionFeeFormat = "0";
        line.feeFormat = "0";

        this.lines.push(line);
        this.rebuildIndex();
        this.tableView.reloadData();
    }

    // Saving the template rewrites the fee rows of every license linked to it,
    // so the venues always match the template.
    private saveAction(){
        DBHelper.saveMainContext();

        let predicate = MIOPredicate.predicateWithFormat('paymentFeeTemplate.identifier == "' + this.template.identifier + '" and deletedAt == null');
        DBHelper.queryObjectsWithCompletion("BusinessLicense", null, predicate, ["paymentFees"], this, function(this:PaymentFeeTemplateDetailViewController, licenses:BusinessLicense[]){
            if (licenses.length == 0) return;

            let lines = this.lines.filter(l => l.deletedAt == null);
            for (let license of licenses) this.template.applyToBusinessLicense(license, lines);
            DBHelper.saveMainContext();
        });
    }
}
